import { useState, useEffect } from 'react';
import { Cookie, X, Settings, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, can't be disabled
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // Show banner after a short delay for better UX
      setTimeout(() => setShowBanner(true), 1000);
    } else {
      // Load existing preferences and initialize tracking
      try {
        const savedPrefs = JSON.parse(consent);
        if (savedPrefs && typeof savedPrefs === 'object' && savedPrefs.necessary !== undefined) {
          setPreferences(savedPrefs);
          initializeTracking(savedPrefs);
        } else {
          // Invalid format, clear and show banner
          localStorage.removeItem('cookieConsent');
          setTimeout(() => setShowBanner(true), 1000);
        }
      } catch (e) {
        // Invalid JSON, clear and show banner
        localStorage.removeItem('cookieConsent');
        setTimeout(() => setShowBanner(true), 1000);
      }
    }
  }, []);

  const initializeTracking = (prefs) => {
    if (prefs.analytics || prefs.marketing) {
      // Load Google Analytics and Ads only if consent given
      if (!window.gtag) {
        const script = document.createElement('script');
        script.async = true;
        script.src = 'https://www.googletagmanager.com/gtag/js?id=G-CZJG538RQ7';
        document.head.appendChild(script);

        script.onload = () => {
          window.dataLayer = window.dataLayer || [];
          function gtag(){window.dataLayer.push(arguments);}
          window.gtag = gtag;
          gtag('js', new Date());

          if (prefs.analytics) {
            gtag('config', 'G-CZJG538RQ7');
          }

          if (prefs.marketing) {
            gtag('config', 'AW-17779264927');
          }
        };
      }
    }
  };

  const saveConsent = (prefs) => {
    localStorage.setItem('cookieConsent', JSON.stringify(prefs));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setPreferences(prefs);
    initializeTracking(prefs);
    setShowBanner(false);
    setShowSettings(false);
  };

  const acceptAll = () => {
    saveConsent({
      necessary: true,
      analytics: true,
      marketing: true,
    });
  };

  const rejectAll = () => {
    saveConsent({
      necessary: true,
      analytics: false,
      marketing: false,
    });
  };

  const saveCustom = () => {
    saveConsent(preferences);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-[9998]" />

      {/* Cookie Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6">
        <Card className="max-w-4xl mx-auto shadow-2xl border-2">
          {!showSettings ? (
            // Main Banner
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 mt-1">
                  <Cookie className="w-8 h-8 text-cyan-500" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    We Value Your Privacy
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. 
                    You can choose which cookies to accept. By clicking "Accept All", you consent to our use of cookies. 
                    See our{' '}
                    <a href="/privacy" className="text-cyan-500 hover:text-cyan-600 underline">
                      Privacy Policy
                    </a>{' '}
                    and{' '}
                    <a href="/terms" className="text-cyan-500 hover:text-cyan-600 underline">
                      Terms of Service
                    </a>{' '}
                    for more information.
                  </p>
                  
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      onClick={acceptAll}
                      className="bg-cyan-500 hover:bg-cyan-600 text-white"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Accept All
                    </Button>
                    <Button
                      onClick={rejectAll}
                      variant="outline"
                      className="border-gray-300 text-gray-700 hover:bg-gray-100"
                    >
                      Reject All
                    </Button>
                    <Button
                      onClick={() => setShowSettings(true)}
                      variant="outline"
                      className="border-gray-300 text-gray-700 hover:bg-gray-100"
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Customize
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // Settings Panel
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">Cookie Preferences</h3>
                <button
                  onClick={() => setShowSettings(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                {/* Necessary Cookies */}
                <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                  <input
                    type="checkbox"
                    checked={true}
                    disabled
                    className="mt-1 w-5 h-5 text-cyan-500 border-gray-300 rounded focus:ring-cyan-500 opacity-50 cursor-not-allowed"
                  />
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-1">
                      Necessary Cookies
                      <span className="ml-2 text-xs font-normal text-gray-500">(Always Active)</span>
                    </h4>
                    <p className="text-sm text-gray-600">
                      These cookies are essential for the website to function properly. They enable basic functions like 
                      page navigation, secure login, and form submissions.
                    </p>
                  </div>
                </div>

                {/* Analytics Cookies */}
                <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                    className="mt-1 w-5 h-5 text-cyan-500 border-gray-300 rounded focus:ring-cyan-500 cursor-pointer"
                  />
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-1">Analytics Cookies</h4>
                    <p className="text-sm text-gray-600">
                      These cookies help us understand how visitors interact with our website by collecting and reporting 
                      information anonymously. This includes Google Analytics.
                    </p>
                  </div>
                </div>

                {/* Marketing Cookies */}
                <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                  <input
                    type="checkbox"
                    checked={preferences.marketing}
                    onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                    className="mt-1 w-5 h-5 text-cyan-500 border-gray-300 rounded focus:ring-cyan-500 cursor-pointer"
                  />
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-1">Marketing Cookies</h4>
                    <p className="text-sm text-gray-600">
                      These cookies are used to track visitors across websites for marketing purposes and to display 
                      relevant advertisements. This includes Google Ads.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={saveCustom}
                  className="bg-cyan-500 hover:bg-cyan-600 text-white"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Save Preferences
                </Button>
                <Button
                  onClick={acceptAll}
                  variant="outline"
                  className="border-gray-300 text-gray-700 hover:bg-gray-100"
                >
                  Accept All
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </>
  );
};

export default CookieConsent;
